/* eslint-disable react-hooks/rules-of-hooks */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { getPremiumContentFor } from "../utils/premiumContent";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";

const SavePremiumPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [premiumData, setPremiumData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const sessionId = searchParams.get("session_id");
        if (!sessionId) {
          throw new Error("No session ID provided");
        }

        let validURL = "verify-payment";
        if (import.meta.env.VITE_ENVIRONMENT === "development") {
          validURL = "verify-payment-test";
        }

        const { data: session, error: sessionError } =
          await supabase.functions.invoke(validURL, {
            body: { sessionId },
          });

        if (sessionError) throw sessionError;

        if (!session || session.payment_status !== "paid") {
          throw new Error("Payment verification failed");
        }

        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("email", session.customer_email)
          .single();

        if (userError) throw userError;

        // Get the latest test result for this user
        const { data: testResult, error: testError } = await supabase
          .from("test_results")
          .select("*")
          .eq("user_id", userData.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (testError) throw testError;

        // Update user's premium status
        const { error: updateError } = await supabase
          .from("users")
          .update({
            has_premium: true,
            premium_purchased_at: new Date().toISOString(),
            premium_report_data: {
              archetype: testResult.primary_archetype,
              content: getPremiumContentFor(testResult.primary_archetype),
            },
          })
          .eq("id", userData.id);

        if (updateError) throw updateError;

        const content = getPremiumContentFor(testResult.primary_archetype);
        console.log("User primary_archetype:", testResult.primary_archetype);
        console.log("Premium content loaded:", content);
        setPremiumData({
          archetype: testResult.primary_archetype,
          content,
        });

        if (
          session.customer_email &&
          session.payment_status == "paid" &&
          testResult.primary_archetype !== ""
        ) {
          console.log("Payment verified for email:", session.customer_email);
          const recipientEmail = session.customer_email;
          const archetype = testResult.primary_archetype || "jaguar";
          const { data, error } = await supabase.functions.invoke(
            "send-premium-report-email-es",
            {
              body: {
                recipientEmail: recipientEmail.trim(),
                archetype,
                first_name: userData.first_name || "",
                last_name: userData.last_name || "",
              },
            }
          );

          if (error) throw error;

          // Example data to encrypt
          const reportData = JSON.stringify({
            recipientEmail: recipientEmail.trim(),
            reportId: testResult.id,
          });
          const secretKey =
            import.meta.env.VITE_SECRET_KEY || "your-strong-secret-key"; // Store securely
          const encrypted = CryptoJS.AES.encrypt(
            reportData,
            secretKey
          ).toString();

          navigate(
            "/premium-success?encrypted=" + encodeURIComponent(encrypted)
          );
          console.log(data);
        } else {
          throw new Error("Payment session is not valid or email is missing");
        }
      } catch (err) {
        console.error("Premium verification error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [navigate, searchParams]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando su compra...</p>
        </div>
      </div>
    );
  }

  if (error || !premiumData) {
    return (
      <>
        <div className="min-h-screen bg-orange-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {error || "Unable to load premium content"}
            </h2>
            <p className="text-gray-600 mb-6">
              Para obtener ayuda, contacte con el equipo de soporte en support@latinocareertest.com. Incluya su correo electrónico de confirmación de pago al contactar con el equipo de soporte.
            </p>
            <a
              href="/"
              className="inline-block bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition-colors"
            >
              Regresar a la página de inicio
            </a>
          </div>
        </div>
      </>
    );
  }

  // If premiumData exists and there is no error, redirect to home
  useEffect(() => {
    if (premiumData && !error) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiumData, error]);

  // Render nothing while redirecting
  return null;
};

export default SavePremiumPage;
